import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  listWatchlists,
  createWatchlist,
  updateWatchlist,
  deleteWatchlist,
  addTickerToWatchlist,
  removeTickerFromWatchlist,
  digestPreview,
  sendDigestEmail,
} from "@/lib/api";
import { Plus, X, Mail, Loader2, Bookmark, Layers } from "lucide-react";
import WatchlistsSidebar from "./WatchlistsSidebar";
import WatchlistControlsBar from "./WatchlistControlsBar";
import WatchlistDigestTable from "./WatchlistDigestTable";

export default function WatchlistsPanel({ activeSymbol, onPickTicker, onPinAll }) {
  const [watchlists, setWatchlists] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [adding, setAdding] = useState(false);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pinningAll, setPinningAll] = useState(false);
  const [ogInterval, setOgInterval] = useState("ytd");

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listWatchlists();
      setWatchlists(data.watchlists || []);
      if (!selectedId && data.watchlists?.length) setSelectedId(data.watchlists[0].watchlist_id);
    } catch {
      toast.error("Failed to load watchlists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const onReload = () => refresh();
    window.addEventListener("watchlists:reload", onReload);
    return () => window.removeEventListener("watchlists:reload", onReload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = watchlists.find((w) => w.watchlist_id === selectedId) || null;
  const currentCadence =
    selected?.cadence || (selected?.email_digest === false ? "off" : "weekly");
  const shareUrl = selected
    ? `${window.location.origin}/api/share/wl/${selected.watchlist_id}?i=${ogInterval}`
    : "";

  // Load digest when selection / ticker count changes
  useEffect(() => {
    if (!selected) {
      setDigest(null);
      return;
    }
    let cancelled = false;
    setDigestLoading(true);
    digestPreview(selected.watchlist_id)
      .then((d) => !cancelled && setDigest(d))
      .catch(() => !cancelled && setDigest(null))
      .finally(() => !cancelled && setDigestLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedId, selected?.tickers?.length]);

  const handleCreate = async () => {
    const n = newName.trim();
    if (!n) return;
    try {
      const wl = await createWatchlist(n, []);
      setWatchlists((p) => [...p, wl]);
      setSelectedId(wl.watchlist_id);
      setNewName("");
      setCreating(false);
      toast.success(`Watchlist "${wl.name}" created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const handleRename = async (id) => {
    const n = renameValue.trim();
    if (!n) return;
    try {
      const updated = await updateWatchlist(id, { name: n });
      setWatchlists((p) => p.map((w) => (w.watchlist_id === id ? updated : w)));
      setRenamingId(null);
    } catch {
      toast.error("Rename failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this watchlist?")) return;
    try {
      await deleteWatchlist(id);
      setWatchlists((p) => p.filter((w) => w.watchlist_id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleAddCurrent = async () => {
    if (!selected) {
      toast.error("Select a watchlist first");
      return;
    }
    if (!activeSymbol) {
      toast.error("Load a ticker first");
      return;
    }
    if (selected.tickers.includes(activeSymbol)) {
      toast.info(`${activeSymbol} already in "${selected.name}"`);
      return;
    }
    setAdding(true);
    try {
      const updated = await addTickerToWatchlist(selected.watchlist_id, activeSymbol);
      setWatchlists((p) =>
        p.map((w) => (w.watchlist_id === selected.watchlist_id ? updated : w))
      );
      toast.success(`${activeSymbol} added to "${selected.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveTicker = async (sym) => {
    if (!selected) return;
    try {
      const updated = await removeTickerFromWatchlist(selected.watchlist_id, sym);
      setWatchlists((p) =>
        p.map((w) => (w.watchlist_id === selected.watchlist_id ? updated : w))
      );
    } catch {
      toast.error("Remove failed");
    }
  };

  const handleSendDigest = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await sendDigestEmail(selected.watchlist_id);
      toast.success(`Digest emailed for "${selected.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setSending(false);
    }
  };

  const setCadence = async (cadence) => {
    if (!selected) return;
    try {
      const updated = await updateWatchlist(selected.watchlist_id, { cadence });
      setWatchlists((p) =>
        p.map((w) => (w.watchlist_id === selected.watchlist_id ? updated : w))
      );
    } catch {
      toast.error("Failed to update cadence");
    }
  };

  const togglePublic = async () => {
    if (!selected) return;
    try {
      const updated = await updateWatchlist(selected.watchlist_id, {
        is_public: !selected.is_public,
      });
      setWatchlists((p) =>
        p.map((w) => (w.watchlist_id === selected.watchlist_id ? updated : w))
      );
      toast.success(updated.is_public ? "Public link enabled" : "Public link disabled");
    } catch {
      toast.error("Failed");
    }
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy link:", shareUrl);
    }
  };

  const handlePinAll = async () => {
    if (!selected || !onPinAll) return;
    if (selected.tickers.length === 0) {
      toast.error("Watchlist is empty");
      return;
    }
    setPinningAll(true);
    try {
      const count = await onPinAll(selected.tickers);
      if (count > 0) {
        toast.success(
          `Pinned ${count} ticker${count === 1 ? "" : "s"} to Compare`
        );
      }
    } catch (e) {
      toast.error(e?.message || "Pin all failed");
    } finally {
      setPinningAll(false);
    }
  };

  return (
    <section data-testid="watchlists-panel" className="mb-12">
      <div className="text-label mb-3 flex items-center gap-2">
        <Bookmark size={12} /> My Watchlists
      </div>

      <div className="border border-zinc-800 bg-[#121214] grid grid-cols-1 lg:grid-cols-[260px_1fr]">
        <WatchlistsSidebar
          watchlists={watchlists}
          selectedId={selectedId}
          onSelect={setSelectedId}
          loading={loading}
          creating={creating}
          setCreating={setCreating}
          newName={newName}
          setNewName={setNewName}
          onCreate={handleCreate}
          renamingId={renamingId}
          setRenamingId={setRenamingId}
          renameValue={renameValue}
          setRenameValue={setRenameValue}
          onRename={handleRename}
          onDelete={handleDelete}
        />

        <div className="min-w-0">
          {!selected ? (
            <div className="p-10 text-center text-sm text-zinc-500">
              Select or create a watchlist to see the weekly digest.
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <div className="text-label">Watchlist</div>
                  <div className="text-lg font-bold tracking-tight truncate">{selected.name}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid="pin-all-to-compare-btn"
                    onClick={handlePinAll}
                    disabled={pinningAll || selected.tickers.length === 0 || !onPinAll}
                    className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-500 text-zinc-300 hover:text-white px-3 py-2 text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Pin all tickers to Compare"
                  >
                    {pinningAll ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Layers size={12} />
                    )}
                    Pin All
                  </button>
                  <button
                    data-testid="add-current-to-watchlist-btn"
                    onClick={handleAddCurrent}
                    disabled={adding || !activeSymbol}
                    className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-500 text-zinc-300 hover:text-white px-3 py-2 text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title={activeSymbol ? `Add ${activeSymbol}` : "Load a ticker first"}
                  >
                    {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                    Add {activeSymbol || "current"}
                  </button>
                  <button
                    data-testid="send-digest-btn"
                    onClick={handleSendDigest}
                    disabled={
                      sending || selected.tickers.length === 0 || currentCadence === "off"
                    }
                    className="inline-flex items-center gap-1.5 bg-white text-black hover:bg-zinc-200 px-3 py-2 text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                    Email digest
                  </button>
                </div>
              </div>

              <WatchlistControlsBar
                cadence={currentCadence}
                onChangeCadence={setCadence}
                isPublic={selected.is_public}
                onTogglePublic={togglePublic}
                shareUrl={shareUrl}
                copied={copied}
                onCopy={copyShareUrl}
                ogInterval={ogInterval}
                onChangeOgInterval={setOgInterval}
                watchlistId={selected.watchlist_id}
              />

              {/* Ticker chips */}
              <div className="px-5 py-3 border-b border-zinc-900 flex flex-wrap gap-2 min-h-[44px] items-center">
                {selected.tickers.length === 0 ? (
                  <div className="text-xs text-zinc-500">
                    Empty. Load a ticker and click <span className="font-mono text-zinc-300">Add</span>.
                  </div>
                ) : (
                  selected.tickers.map((sym) => (
                    <span
                      key={sym}
                      data-testid={`wl-ticker-${sym}`}
                      className="inline-flex items-center gap-1.5 border border-zinc-800 bg-[#0d0d0f] px-2 py-1 text-xs font-mono"
                    >
                      <button
                        onClick={() => onPickTicker?.(sym)}
                        className="hover:text-white text-zinc-200"
                        title="Load this ticker"
                      >
                        {sym}
                      </button>
                      <button
                        onClick={() => handleRemoveTicker(sym)}
                        className="text-zinc-500 hover:text-[#FF3B30]"
                        title="Remove"
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))
                )}
              </div>

              <WatchlistDigestTable
                digest={digest}
                loading={digestLoading}
                onPickTicker={onPickTicker}
              />
            </>
          )}
        </div>
      </div>
    </section>
  );
}
